import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { Reveal } from './Reveal';
import { GlowFrame } from './GlowFrame';
import { PhotoLightbox } from './PhotoLightbox';
import { GALLERY_IMAGES } from '../data/gallery';
export function GallerySection() {
  const trackRef = useRef<HTMLDivElement>(null);
  const slideRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const isDragging = useRef(false);
  const dragStart = useRef({
    x: 0,
    scrollLeft: 0
  });
  const scrollToIndex = useCallback((index: number) => {
    const clamped = Math.max(0, Math.min(index, GALLERY_IMAGES.length - 1));
    slideRefs.current[clamped]?.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest'
    });
  }, []);
  const updateActiveFromScroll = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const trackCenter = track.scrollLeft + track.clientWidth / 2;
    let closestIndex = 0;
    let closestDistance = Infinity;
    slideRefs.current.forEach((slide, index) => {
      if (!slide) return;
      const slideCenter = slide.offsetLeft + slide.clientWidth / 2;
      const distance = Math.abs(slideCenter - trackCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });
    setActiveIndex(closestIndex);
  }, []);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let frame: number;
    const handleScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(updateActiveFromScroll);
    };
    track.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => {
      track.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(frame);
    };
  }, [updateActiveFromScroll]);
  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const track = trackRef.current;
    if (!track) return;
    isDragging.current = true;
    dragStart.current = {
      x: event.clientX,
      scrollLeft: track.scrollLeft
    };
    track.setPointerCapture(event.pointerId);
  };
  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging.current || !trackRef.current) return;
    const delta = event.clientX - dragStart.current.x;
    trackRef.current.scrollLeft = dragStart.current.scrollLeft - delta;
  };
  const handlePointerUp = () => {
    isDragging.current = false;
  };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      scrollToIndex(activeIndex - 1);
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      scrollToIndex(activeIndex + 1);
    }
  };
  return <section id="gallery" aria-label="Production gallery" className="bg-ink px-5 py-24 sm:px-8 lg:px-12 lg:py-32" data-id="element-110" data-mp="vnklopbd0q0s">
      <Reveal data-id="element-111" data-mp="2g427jt84t875">
        <p className="mb-3 text-center text-[11px] uppercase tracking-[0.3em] text-ice" data-id="element-112" data-mp="2g3siq8ytp731">Photography</p>
        <h2 className="mb-12 text-center font-heading text-4xl uppercase tracking-tight text-led sm:text-5xl" data-id="element-113" data-mp="2pvy4h0yhes9s">
          Gallery
        </h2>
      </Reveal>

      <Reveal delay={0.1} data-id="element-114" data-mp="2l3n6i3naxgjv">
        <div className="relative mx-auto max-w-[1800px]" data-id="element-115" data-mp="2l3esg8fg0573">
          <div ref={trackRef} role="region" aria-roledescription="carousel" aria-label="Production photographs" tabIndex={0} onKeyDown={handleKeyDown} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} onPointerLeave={handlePointerUp} className="scrollbar-none flex cursor-grab select-none gap-6 overflow-x-auto scroll-smooth focus:outline-none focus-visible:ring-2 focus-visible:ring-ice active:cursor-grabbing" style={{
          scrollSnapType: 'x mandatory'
        }} data-id="element-116" data-mp="2yvbh22hwjwyq">
            <div className="shrink-0" style={{
            width: 'calc(50% - 1px)'
          }} aria-hidden="true" data-id="element-117" data-mp="1fxonbeduq0ye" />
            {GALLERY_IMAGES.map((image, index) => <div key={image.src} ref={el => {
            slideRefs.current[index] = el;
          }} className="flex h-[62vh] shrink-0 items-center" style={{
            scrollSnapAlign: 'center'
          }} data-id="element-118" data-mp="1kxkshgyabyfj" data-mpi={index}>
                <button type="button" onClick={() => setLightboxIndex(index)} className="h-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ice" aria-label={`Open ${image.alt} in full screen`} data-id="element-119" data-mp="x8icedjaudn5">
                  <motion.img src={image.src} alt={image.alt} loading="lazy" animate={{
                opacity: activeIndex === index ? 1 : 0.35
              }} transition={{
                duration: 0.3,
                ease: [0.16, 1, 0.3, 1]
              }} className="h-full w-auto object-contain" data-id="element-120" data-mp="9h384f7ypm99" />
                </button>
              </div>)}
            <div className="shrink-0" style={{
            width: 'calc(50% - 1px)'
          }} aria-hidden="true" data-id="element-121" data-mp="2isng00huwxst" />
          </div>

          <div className="mt-8 hidden items-center justify-center gap-6 sm:flex" data-id="element-122" data-mp="2dtty7vvjekmu">
            <GlowFrame as="button" interactive onClick={() => scrollToIndex(activeIndex - 1)} disabled={activeIndex === 0} aria-label="Previous photograph" className="flex h-11 w-11 items-center justify-center rounded-full text-led disabled:opacity-30" data-id="element-123" data-mp="28to7chx3d25t">
              <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-124" data-mp="1q251827rt7zk" />
            </GlowFrame>
            <div className="h-px w-64 bg-led/15" data-id="element-125" data-mp="1l2ae89r492js">
              <motion.div className="h-px bg-ice" animate={{
              width: `${(activeIndex + 1) / GALLERY_IMAGES.length * 100}%`
            }} transition={{
              duration: 0.4,
              ease: [0.16, 1, 0.3, 1]
            }} data-id="element-126" data-mp="1l4vggzvbtki2" />
            </div>
            <GlowFrame as="button" interactive onClick={() => scrollToIndex(activeIndex + 1)} disabled={activeIndex === GALLERY_IMAGES.length - 1} aria-label="Next photograph" className="flex h-11 w-11 items-center justify-center rounded-full text-led disabled:opacity-30" data-id="element-127" data-mp="12fjl8m1a9297">
              <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-128" data-mp="1ldm8ava9sp4d" />
            </GlowFrame>
          </div>

          <div className="mt-6 h-px w-full bg-led/15 sm:hidden" data-id="element-129" data-mp="1pe61xovmxalr">
            <motion.div className="h-px bg-ice" animate={{
            width: `${(activeIndex + 1) / GALLERY_IMAGES.length * 100}%`
          }} transition={{
            duration: 0.4,
            ease: [0.16, 1, 0.3, 1]
          }} data-id="element-130" data-mp="1pcl9q5wsm3r2" />
          </div>
        </div>
      </Reveal>

      <PhotoLightbox images={GALLERY_IMAGES} activeIndex={lightboxIndex} onClose={() => setLightboxIndex(null)} onChange={setLightboxIndex} data-id="element-131" data-mp="6egheeilxhfn" />
    </section>;
}